import { useEffect, useState } from "react";
import { HasAccess } from "@/components/common/has-access";
import { useSearchParams } from "react-router-dom";
import {
  useNotifications,
  useBulkUpdateNotificationStatus,
  useUpdateNotificationTags,
} from "../hooks/use-notifications";
import NotificationCard from "./notification-card";
import { SearchBar } from "@/components/common/search-bar";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import NotificationFilters from "./notification-filters";
import {
  NotificationFilters as NotificationFiltersInterface,
  Notification,
} from "@/pages/notifications/types/notification";
import { Button } from "@/components/ui/button";
import { XIcon, Download } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { useExportNotificationsCsv } from "../hooks/use-notifications";

const NotificationList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hoveredNotificationId, setHoveredNotificationId] = useState<
    string | null
  >(null);

  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || "ALL";
  const startDate = searchParams.get("startDate") || undefined;
  const endDate = searchParams.get("endDate") || undefined;
  const tagsParam = searchParams.get("tags");
  const tags =
    tagsParam === "true" ? true : tagsParam === "false" ? false : undefined;
  const template = searchParams.get("template") || undefined;
  const type = searchParams.get("type") || undefined;

  const { data, isLoading, isFetching, error } = useNotifications({
    page,
    search,
    status:
      status === "ALL"
        ? undefined
        : (status as NotificationFiltersInterface["status"]),
    startDate,
    endDate,
    tags,
    template,
    type,
  });
  const { mutate: updateNotificationTags } = useUpdateNotificationTags(
    (ids) => {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    }
  );

  const { mutate: bulkUpdateStatus, isPending } =
    useBulkUpdateNotificationStatus();

  const { mutate: exportNotifications, isPending: isExporting } =
    useExportNotificationsCsv();

  const [selected, setSelected] = useState<string[]>([]);

  const handleSelect = (notification: Notification, checked: boolean) => {
    const updated = new Set(selected);

    if (checked) {
      updated.add(notification.id);

      if (notification.linked_notification_id) {
        updated.add(notification.linked_notification_id);
      }
    } else {
      updated.delete(notification.id);

      if (notification.linked_notification_id) {
        updated.delete(notification.linked_notification_id);
      }
    }

    setSelected(Array.from(updated));
  };

  const handleBulkSend = () => {
    if (selected.length === 0) return;
    bulkUpdateStatus(
      { ids: selected, status: "PENDING" },
      { onSuccess: () => setSelected([]) }
    );
  };
  const handleBulkHold = () => {
    if (selected.length === 0) return;
    bulkUpdateStatus(
      { ids: selected, status: "HOLD" },
      { onSuccess: () => setSelected([]) }
    );
  };

  useEffect(() => {
    return () => {
      setSelected([]);
    };
  }, [searchParams]);

  const notifications = data?.notifications || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const activeSelectedNotifications = notifications.filter((n: Notification) =>
    selected.includes(n.id)
  );

  const showBulkSend = activeSelectedNotifications.some(
    (n: Notification) => n.status === "HOLD"
  );
  const showBulkHold = activeSelectedNotifications.some(
    (n: Notification) => n.status === "PENDING"
  );
  const showClearSelection = activeSelectedNotifications.length > 0;

  const handleExport = () => {
    const params: any = {};
    const hasFilters =
      searchParams.get("search") ||
      searchParams.get("status") ||
      searchParams.get("startDate") ||
      searchParams.get("endDate") ||
      searchParams.get("template") ||
      searchParams.get("type");

    if (hasFilters) {
      if (searchParams.get("search"))
        params.search = searchParams.get("search");
      if (searchParams.get("status"))
        params.status = searchParams.get("status");
      if (searchParams.get("startDate"))
        params.startDate = searchParams.get("startDate");
      if (searchParams.get("endDate"))
        params.endDate = searchParams.get("endDate");
      if (searchParams.get("template"))
        params.template = searchParams.get("template");
      if (searchParams.get("type")) params.type = searchParams.get("type");
    }

    exportNotifications(params);
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <NotificationFilters />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search by Notifications..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
              // fieldSelector={{
              //   enabled: true,
              //   fields: [
              //     { value: "name", label: "Name" },
              //     { value: "email", label: "Email" },
              //     { value: "phone", label: "Phone" },
              //   ],
              //   allowAll: true,
              // }}
            />
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            disabled={isExporting}
            onClick={handleExport}
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
          {showClearSelection && (
            <div className="flex justify-end space-x-2">
              {showBulkSend && (
                <HasAccess permission="write">
                <Button
                  onClick={handleBulkSend}
                  disabled={isPending}
                  variant="default"
                >
                  {isPending
                    ? "Updating..."
                    : `Move ${selected.length} to Pending`}
                </Button>
                </HasAccess>
              )}
              {showBulkHold && (
                <HasAccess permission="write">
                <Button
                  onClick={handleBulkHold}
                  disabled={isPending}
                  variant="default"
                >
                  {isPending
                    ? "Updating..."
                    : `Move ${selected.length} to Hold`}
                </Button>
                </HasAccess>
              )}
              <Button onClick={() => setSelected([])} variant="outline">
                Clear Selection
              </Button>
            </div>
          )}
        </div>
      }
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load notifications"
      emptyState={{
        message:
          "No notifications found. Try adjusting your search criteria or filters.",
      }}
    >
      <div className="space-y-4">
        {notifications.map((notification: Notification) => (
          <NotificationCard
            key={notification.id}
            id={notification.id}
            notification={notification}
            selectable={
              (notification.status === "HOLD" ||
                notification.status === "PENDING") &&
              notification.recipient_type === "PROFESSIONAL"
            }
            selected={selected.includes(notification.id)}
            onSelect={handleSelect}
            hoveredNotificationId={hoveredNotificationId}
            setHoveredNotificationId={setHoveredNotificationId}
            onUpdateTags={updateNotificationTags}
          />
        ))}
      </div>
      {notifications.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={(newPage) => {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set("page", newPage.toString());
            setSearchParams(newSearchParams);
          }}
        />
      )}
    </ContentLayout>
  );
};

export default NotificationList;
